import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import {
  AlertTriangle,
  ArrowLeft,
  Box as BoxIcon,
  Camera,
  Check,
  Menu,
  Printer,
  RefreshCw,
  X,
} from "lucide-react";
import React, { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import * as fbs from "@/api/fbs";
import { Button } from "@/components/ui/button";
import {
  boxBlockReason,
  boxWeightLabel,
  createPartiaMemory,
  heroForResult,
  ISSUE_GROUPS,
  useRetryAllIssues,
  ordersWord,
  PICK_ERROR_TITLE,
  rememberPartia,
  sgtinPollInterval,
  sgtinSupplyLabel,
  sortScanRows,
  suggestPartia,
  type HeroState,
  useSyncOrders,
} from "@/lib/fbs";
import { parseScan } from "@/lib/order-scan";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import {
  AccessDenied,
  apiErrorText,
  isForbidden,
  LoadState,
  useGoBack,
} from "@/lib/scan-ui/screen";
import { fmtQty } from "@/lib/scan-ui/status";
import { useScanCapture } from "@/lib/use-scan-capture";
import { cn } from "@/lib/utils";
import { ScanBanner, ScanBannerSlot } from "@/pages/fbs/ScanBanner";
import { RemoveSgtinButton } from "@/pages/fbs/RemoveSgtinButton";
import { ReplaceSgtinButton } from "@/pages/fbs/ReplaceSgtinDialog";
import { SgtinRejectedAlert } from "@/pages/fbs/SgtinRejectedAlert";

// Кладовщик ФБС работает с телефона, поэтому камера — полноправный путь скана,
// наравне со сканером ТСД.

/**
 * S2 — сбор в комнате ФБС. Один вьюпорт: скроллится только список, панели
 * закреплены. Поля ввода штрихкода нет — работает глобальный перехват скана.
 *
 * Экран привязан к ЮРЛИЦУ (`/fbs/cabinets/{id}/pick`), а не к поставке:
 * поставка заводится ПЕРВЫМ сканом (`POST /cabinets/{id}/pick`). Зашёл и ушёл —
 * в WB ничего не осталось.
 *
 * Главное на экране — СПИСОК К СБОРУ: кладовщик по нему ходит по комнате.
 * Результат скана ложится плашкой поверх списка (см. ScanBanner) и список не
 * прячет. Ошибки и незакрытые хвосты сами не исчезают: пропустить «стикер не
 * напечатан» — значит привезти в ПВЗ штуку, которую не примут.
 */
/**
 * Перепечатка стикера ЗАДАНИЯ. Одна на весь экран.
 *
 * ⚠️ Была написана дважды — в карточке последнего скана и в шторке короба, — и
 * обе звались `reprintMut`, хотя в шторке так же зовётся печать QR ГРУЗОМЕСТА:
 * три мутации, два имени, один файл. `onDone` нужен только карточке (она
 * перечитывает состояние экрана), шторка обходится без него.
 */
function useReprintSticker(onDone?: () => void) {
  return useMutation({
    mutationFn: (pickId: string) => fbs.reprintPickSticker(pickId),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success("Стикер отправлен на печать");
        onDone?.();
      } else {
        toast.error(r.error ?? "Печать не удалась");
      }
    },
    onError: (e) => toast.error(apiErrorText(e, "Печать не удалась")),
  });
}

export function FbsPickPage(): React.ReactElement {
  // Чанк камеры тянем при ВХОДЕ: устаревший чанк после выката
  // перезагружает страницу, и делать это в момент тапа «Сканировать»
  // значит прерывать работу ровно посередине.
  usePreloadChunk(CameraScanner);
  const { cabinetId = "" } = useParams();
  const cabinet = Number(cabinetId);
  const navigate = useNavigate();
  const goBack = useGoBack("/fbs");
  const qc = useQueryClient();

  const [hero, setHero] = useState<HeroState>({ kind: "idle" });
  // Код поймал перехват выключенным (летит предыдущий скан, открыта камера/
  // шторка) — отдельно от `hero`: незасчитанный скан не должен стирать героя
  // последнего ЗАСЧИТАННОГО (и с ним — «перепечатать стикер», см. `lastPickId`
  // ниже). Гаснет по новому засчитанному скану или руками — как и сам `hero`.
  const [blockedNotice, setBlockedNotice] = useState<string | null>(null);
  // Артикул последнего скана: его строка уезжает наверх списка, чтобы было
  // видно, сколько этого товара ещё осталось (см. sortScanRows).
  const [lastArticle, setLastArticle] = useState<string | null>(null);
  const [sheet, setSheet] = useState<null | "box" | "fix" | "menu">(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  // Открыт диалог «Заменить товар»: скан там свой, основной цикл его не ловит.
  const [replacing, setReplacing] = useState(false);
  // Немаркированный товар: скан EAN13 запросом НЕ становится — сначала оператор
  // вводит партию с этикетки (в штрихкоде её нет).
  const [eanDraft, setEanDraft] = useState<null | { code: string; partia: string }>(null);
  // Ответ 409 `partia_not_found`: в WB и в БД ничего не создано, задание не
  // занято и стикер не напечатан — поэтому повтор с подтверждением безопасен.
  const [partiaMissing, setPartiaMissing] = useState<
    null | { code: string; partia: string; detail: string }
  >(null);
  // Последняя партия по артикулу — только в пределах экрана (см. lib/fbs.ts).
  const partiaMemory = useRef(createPartiaMemory());
  // Склад и ТИП ПРОДАЖИ живут в URL, а не в состоянии экрана: оба выбираются
  // на главной и обязаны пережить перезагрузку. Скрытое состояние означало бы
  // «после F5 ты молча на первом складе / в обычной поставке» — а это тихая
  // пересортица: у складов свои поставки, у B2B — своя.
  const [searchParams, setSearchParams] = useSearchParams();
  const whParam = Number(searchParams.get("wh"));
  const warehouse = Number.isFinite(whParam) && whParam > 0 ? whParam : null;
  const isB2b = searchParams.get("b2b") === "1";
  const setWarehouse = (id: number) =>
    setSearchParams(
      { wh: String(id), ...(isB2b ? { b2b: "1" } : {}) },
      { replace: true },
    );

  // Одна ручка на всё состояние экрана: поставка, список, «Разбор». Раньше это
  // были три запроса, и все три перечитывались после каждого скана.
  // Ключ состояния экрана — ОДИН на файл: оптимистичные записи из шторок
  // обязаны попадать в ту же ячейку, что читает экран, иначе «мгновенный
  // ответ» уходит в пустоту и всё держится на последующем рефетче.
  const stateKey = ["fbs-pick-state", cabinet, warehouse, isB2b];
  const stateQ = useQuery({
    queryKey: stateKey,
    queryFn: () => fbs.getPickState(cabinet, warehouse, isB2b),
  });
  const state = stateQ.data;
  const supply = state?.supply ?? null;

  // Остаток комнаты — обращение в 1С и всего лишь подсказка «где искать».
  // Держим его ХОЛОДНЫМ: 5 минут кэша, без перезапроса на фокус окна и без
  // инвалидации после сканов — иначе он оказался бы на самом горячем пути.
  const roomQ = useQuery({
    queryKey: ["fbs-room-stock", cabinet, warehouse],
    queryFn: () => fbs.getRoomStock(cabinet, warehouse),
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  // Наименования к артикулам — тоже обращение в 1С и тоже подсказка. Держим
  // ХОЛОДНЫМИ по тем же правилам, что и остаток комнаты: наименования не
  // меняются, а список перечитывается после каждой собранной штуки.
  const namesQ = useQuery({
    queryKey: ["fbs-article-names"],
    queryFn: fbs.getArticleNames,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  // Агент печати может отвалиться посреди сбора, а печать идёт на каждый скан —
  // прочитанный один раз при монтировании статус врал бы весь остаток смены.
  //
  // Спрашиваем ПРО ТОЧКУ склада, если он ей отдан: стикер уйдёт на её принтер
  // через её агента, и состояние общего агента на этом экране — чужое. Точка
  // приходит в `pick-state`, поэтому до его загрузки статус не спрашиваем
  // вовсе: ответ про общий агент был бы не про этот экран.
  const point = state?.point ?? null;
  const printQ = useQuery({
    queryKey: ["fbs-print-status", point?.id ?? null],
    queryFn: () => fbs.getPrintStatus(point?.id ?? null),
    enabled: stateQ.isSuccess,
    refetchInterval: 30000,
  });
  const configQ = useQuery({
    queryKey: ["fbs-config"],
    queryFn: fbs.getConfig,
    staleTime: 300000,
  });
  const scanMethod = configQ.data?.scan_method ?? "tsd";
  // Партии есть не в каждой базе 1С. Где их нет, спрашивать номер бессмысленно:
  // бэкенд его игнорирует, а кладовщик набирал бы выдуманное число на КАЖДОЙ
  // штуке — немаркированный товар там основной.
  const asksPartia = configQ.data?.partia ?? true;
  const cameraAllowed = scanMethod === "camera" || scanMethod === "both";

  const activeBox = supply?.active_box ?? null;

  // Фоновая проверка КИЗ у WB (проверка асинхронная, обычно < 1 мин). Опрос
  // идёт, пока есть чего ждать (`sgtinPollInterval`), и перечитывается после
  // каждого скана и действия (`refetchState`). К WB сервер ходит не чаще раза
  // в 15 с на поставку и только по неитоговым — сколько бы телефонов её ни
  // собирали. В `pick-state` ради этого ничего не добавлено: его контракт
  // стережёт снимок `test_fbs_points_guard.py`.
  //
  // ⚠️ Опрос — по ВСЕМ открытым поставкам складов человека, а не по поставке
  // экрана, и идёт даже когда у экрана поставки ещё нет. Экран показывает
  // поставку ОДНОГО юрлица: пилот 24.09.2026 — сборщик собрал поставку одного
  // юрлица, ушёл на экран другого, и КИЗ первой так и остались без итога.
  const supplyId = supply?.supply_id ?? null;
  const sgtinQ = useQuery({
    queryKey: ["fbs-sgtin-poll"],
    queryFn: fbs.pollSgtin,
    refetchInterval: (q) => sgtinPollInterval(q.state.data, q.state.status === "error"),
    retry: false,
  });

  // Сводку главного экрана здесь НЕ трогаем: она самый дорогой запрос модуля,
  // а на экране сбора из неё ничего не читается.
  const refetchState = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["fbs-pick-state", cabinet] });
    // Скан мог отправить КИЗ, действие — убрать его: опрос перечитываем тоже.
    qc.invalidateQueries({ queryKey: ["fbs-sgtin-poll"] });
  }, [qc, cabinet]);

  const pickMut = useMutation({
    mutationFn: (payload: fbs.PickRequest) =>
      fbs.pick(cabinet, { ...payload, wb_warehouse_id: warehouse, b2b: isB2b }),
    onSuccess: (result, payload) => {
      setHero(heroForResult(result));
      setLastArticle(result.article);
      // Артикул приходит только с ответом — здесь и достраивается связка
      // «штрихкод → артикул», по которой партия подставится следующей штуке.
      if (payload.scan_kind === "ean13") {
        rememberPartia(
          partiaMemory.current,
          payload.ean13 ?? "",
          result.article,
          payload.partia_name ?? "",
        );
      }
      refetchState();
    },
    onError: (
      e: { response?: { data?: { detail?: fbs.PickErrorDetail | string } } },
      payload,
    ) => {
      const detail = e.response?.data?.detail;
      // «Партии нет в 1С» — не ошибка скана, а вопрос оператору: товар мог быть
      // напечатан до перехода на партийный учёт. Спрашиваем модалкой.
      if (
        detail &&
        typeof detail === "object" &&
        detail.code === "partia_not_found" &&
        payload.scan_kind === "ean13"
      ) {
        setPartiaMissing({
          code: payload.ean13 ?? "",
          partia: payload.partia_name ?? "",
          detail: detail.detail,
        });
        return;
      }
      if (detail && typeof detail === "object") {
        setHero({
          kind: "error",
          code: detail.code,
          text: detail.detail,
          cabinetName: detail.cabinet_name,
        });
        // Отменённое задание бэк гасит прямо на этом отказе, и «нужно» по
        // артикулу уменьшилось. Без перечитывания список показывал бы старое
        // число, а кладовщик искал бы штуку, которую собирать уже не надо.
        if (detail.code === "order_cancelled") refetchState();
      } else {
        setHero({ kind: "error", code: "error", text: apiErrorText(e, "Ошибка скана") });
      }
    },
  });

  // Задания можно подтягивать не выходя из сбора: пришедшее в середине спокойно
  // уедет текущей поставкой.
  const syncMut = useSyncOrders(refetchState);

  // Закрытие — только отметка границы между коробами. Грузоместа и этикетки
  // заводятся одним заходом на экране завершения поставки.
  const closeBoxMut = useMutation({
    mutationFn: (boxId: string) => fbs.closeBox(boxId),
    onSuccess: (box) => {
      refetchState();
      toast.success(`Короб ${box.position} закрыт. Этикетки печатаются в конце поставки`);
    },
    onError: (e) => toast.error(apiErrorText(e, "Не удалось закрыть короб")),
  });

  // Стикер мог напечататься и потеряться/испортиться — перепечатываем последний
  // без нового скана. Тот же путь чинит и состояние «принтер не ответил».
  const lastPickId = hero.kind === "ok" || hero.kind === "warn" ? hero.result.pick_id : null;
  const reprintMut = useReprintSticker(refetchState);

  const onScan = useCallback(
    (raw: string) => {
      // Дошёл настоящий скан — плашка «предыдущий не засчитан» устарела.
      setBlockedNotice(null);
      // Скан во время загрузки/ошибки нельзя проглатывать молча: кладовщик
      // считает штуку учтённой и кладёт её в короб.
      if (!state) {
        toast.error(
          stateQ.isLoading
            ? "Экран ещё загружается — повторите скан"
            : "Состояние не загружено, скан не принят: нажмите «Повторить»",
        );
        return;
      }
      const parsed = parseScan(raw);
      // Маркированный товар: в коде есть серийник (AI 21) — отправляем сразу.
      if (parsed.kind === "datamatrix" && parsed.gtin) {
        setHero({ kind: "resolving" });
        pickMut.mutate({
          scan_kind: "cim",
          cim_code: parsed.raw,
          gtin: parsed.gtin,
          serial: parsed.serial ?? "",
        });
        return;
      }
      // Немаркированный товар (старые партии): на этикетке только EAN13, партия
      // напечатана рядом текстом. Запрос не шлём — сначала спрашиваем партию,
      // иначе позиция уехала бы в «Разбор» на каждой штуке.
      if (parsed.kind === "ean13" && parsed.ean13) {
        const code = parsed.ean13;
        if (!asksPartia) {
          // Партий в базе нет — скан уходит сразу, как маркированный.
          setHero({ kind: "resolving" });
          pickMut.mutate({ scan_kind: "ean13", ean13: code });
          return;
        }
        setHero({ kind: "idle" });
        setEanDraft({ code, partia: suggestPartia(partiaMemory.current, code) });
        return;
      }
      setHero({
        kind: "error",
        code: "unknown_product",
        text: "Код не распознан: в комнате сканируем Честный знак или штрихкод EAN13 с этикетки",
      });
    },
    [state, stateQ.isLoading, pickMut],
  );

  // Ввод партии закончен — тот же скан уходит веткой EAN13. `force` ставится
  // только по подтверждению человека в модалке «партии нет в 1С».
  const submitEan = useCallback(
    (code: string, partia: string, acceptWithoutPartia: boolean) => {
      setEanDraft(null);
      setPartiaMissing(null);
      setHero({ kind: "resolving" });
      pickMut.mutate({
        scan_kind: "ean13",
        ean13: code,
        partia_name: partia,
        accept_without_partia: acceptWithoutPartia,
      });
    },
    [pickMut],
  );

  // Плашка результата в этот список НЕ входит: она часть разметки, а не шторка,
  // и сканирование при открытой плашке продолжается — иначе незакрытая ошибка
  // останавливала бы сбор. А вот ввод партии и модалка перехват гасят: иначе
  // следующий скан улетел бы символами в поле номера партии.
  const scanEnabled =
    sheet === null &&
    !eanDraft &&
    !partiaMissing &&
    !cameraOpen &&
    !replacing &&
    !pickMut.isPending;
  const { scanInputRef, scanInputProps } = useScanCapture({
    enabled: scanEnabled,
    onScan,
    // ⚠️ Перехват выключен — код НЕ засчитан, и молчать об этом нельзя: на
    // экране сбора «отсканировал и пошёл дальше» стоит непосчитанной штуки.
    // Отдельное состояние экрана, а не `hero`: `hero` — герой ПОСЛЕДНЕГО
    // ЗАСЧИТАННОГО скана (от него зависит «перепечатать стикер», см.
    // `lastPickId`), и затирать его случайным выстрелом с ТСД, пока открыта
    // камера/шторка, нельзя — иначе перепечатать испорченный стикер стало бы
    // нечем. Плашка ложится РЯДОМ с героем (см. рендер ниже), не вместо него.
    onBlocked: () =>
      setBlockedNotice(
        pickMut.isPending
          ? "Предыдущий скан ещё записывается — отсканируйте ещё раз."
          : "Сначала ответьте на вопрос на экране, потом сканируйте.",
      ),
  });
  // Аппаратная «назад» при открытой камере закрывает камеру, а не экран сбора.
  useBackGuard(cameraOpen, () => setCameraOpen(false));

  const blockReason = useMemo(() => boxBlockReason(activeBox), [activeBox]);
  const rows = useMemo(
    () =>
      sortScanRows(state?.rows ?? [], {
        key: (row) => `${row.article}\u0000${row.characteristic_name}`,
        done: (row) => row.needed === 0,
        last: lastArticle,
      }),
    [state?.rows, lastArticle],
  );
  const room = roomQ.data?.stock;
  const names = namesQ.data?.names;
  const ready = state != null;

  // «Собрано X из Y» — по ЭТОЙ поставке: `picked_total` считает все задания в
  // статусе picked за всю историю юрлица (они не чистятся), и через неделю
  // работы показывал бы «собрано 1240 из 1247».
  const picked = supply?.picked ?? 0;
  const left = state?.needed_total ?? 0;

  return (
    <div className="flex h-app flex-col overflow-hidden bg-slate-50">
      <input
        ref={scanInputRef}
        {...scanInputProps}
        className="pointer-events-none fixed left-0 top-0 size-px opacity-0"
      />

      {/* Аппбар. Рендерится ВСЕГДА, в том числе при ошибке загрузки: роут
          иммерсивный, и без этой стрелки с экрана некуда деться. */}
      <header className="flex items-center gap-2 bg-white px-3 py-2 shadow-sm">
        <button type="button" onClick={goBack} aria-label="Назад">
          <ArrowLeft className="size-5" />
        </button>
        <span className="flex-1 truncate text-lg font-semibold uppercase">
          {state?.cabinet_name ?? "ФБС"}
        </span>
        {/* Переключатель склада. Показывается только когда складов больше
            одного: они работают одновременно, у каждого своя поставка, и
            кладовщик переходит между ними, не выходя из скан-цикла. */}
        {(state?.warehouses.length ?? 0) > 1 && (
          <select
            aria-label="Склад продавца"
            className="max-w-[9rem] truncate rounded border px-2 py-1 text-xs"
            value={state?.wb_warehouse_id ?? ""}
            onChange={(e) => setWarehouse(Number(e.target.value))}
          >
            {state?.warehouses.map((w) => (
              <option key={w.wb_warehouse_id} value={w.wb_warehouse_id}>
                {w.name || `Склад ${w.wb_warehouse_id}`}
              </option>
            ))}
          </select>
        )}
        {(state?.issues.total ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => setSheet("fix")}
            className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-900"
          >
            Разбор {state?.issues.total}
          </button>
        )}
        <span
          title={
            printQ.data?.relay_connected
              ? `Принтер готов${point ? ` (точка «${point.name}»)` : ""}`
              : `Агент печати не подключён${point ? ` (точка «${point.name}»)` : ""}`
          }
          className={printQ.data?.relay_connected ? "text-emerald-600" : "text-rose-600"}
        >
          <Printer className="size-5" />
        </span>
        <button type="button" onClick={() => setSheet("menu")} aria-label="Меню">
          <Menu className="size-5" />
        </button>
      </header>

      {/* Принтер не отвечает — это надо заметить не приглядываясь: без стикера
          штуку в ПВЗ не примут, а печать идёт на каждый скан. */}
      {printQ.data && !printQ.data.relay_connected && (
        <div className="bg-rose-600 px-3 py-1 text-center text-xs font-semibold text-white">
          {point
            ? `Агент точки «${point.name}» не подключён — стикеры не печатаются`
            : "Принтер не подключён — стикеры не печатаются"}
        </div>
      )}

      {!ready ? (
        // Отказ по привязке к точке — не сбой загрузки: «Повторить» его не
        // чинит, а текст причины присылает сервер.
        isForbidden(stateQ.error) ? (
          <AccessDenied error={stateQ.error} />
        ) : (
          <LoadState
            isLoading={stateQ.isLoading}
            error={stateQ.error}
            onRetry={() => stateQ.refetch()}
          />
        )
      ) : (
        <>
          {/* Прогресс по поставке */}
          <div className="flex items-center gap-2 border-b bg-white px-3 py-1.5 text-[13px]">
            <span>
              Собрано <b>{picked}</b> из <b>{picked + left}</b>
            </span>
            <span className="ml-auto text-slate-500">осталось {left}</span>
          </div>

          {/* Подсказка «сколько в комнате» может быть недоступна — говорим об
              этом прямо: «не смогли спросить» и «в комнате пусто» — разные вещи. */}
          {(roomQ.data?.error || roomQ.isError) && (
            <p className="bg-amber-50 px-3 py-1 text-[11px] text-amber-800">
              {roomQ.data?.error ?? apiErrorText(roomQ.error, "Остаток комнаты недоступен")}
            </p>
          )}

          {/* Наименований может не быть по той же причине — говорим об этом
              прямо, а не показываем список с одними артикулами как норму. */}
          {(namesQ.data?.error || namesQ.isError) && (
            <p className="bg-amber-50 px-3 py-1 text-[11px] text-amber-800">
              {namesQ.data?.error ??
                apiErrorText(namesQ.error, "Наименования недоступны")}
            </p>
          )}

          {/* Забракованные WB КИЗ — постоянно, пока штуку не отправят без кода. */}
          <SgtinRejectedAlert
            rows={sgtinQ.data?.rejected ?? []}
            currentSupplyId={supplyId}
            onChanged={refetchState}
            extraAction={(row) => (
              <ReplaceSgtinButton
                row={row}
                onOpenChange={setReplacing}
                onReplaced={refetchState}
              />
            )}
          />
          {sgtinQ.isError && (
            <p className="bg-rose-100 px-3 py-1 text-[11px] text-rose-800">
              {apiErrorText(sgtinQ.error, "Проверка КИЗ у WB не удалась")}
            </p>
          )}
          {/* WB не ответил по поставке — остальные опрошены, эта названа. */}
          {(sgtinQ.data?.errors ?? []).map((err) => (
            <p key={err.supply_id} className="bg-rose-100 px-3 py-1 text-[11px] text-rose-800">
              Проверка КИЗ у WB не удалась ({sgtinSupplyLabel(err)}): {err.detail}
            </p>
          ))}
          {(sgtinQ.data?.unresolved ?? 0) > 0 && (
            <p className="bg-slate-100 px-3 py-1 text-[11px] text-slate-600">
              КИЗ на проверке у WB: {sgtinQ.data?.unresolved} шт.
            </p>
          )}

          {/* Главная область — список к сборке. Скроллится только он. */}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            {rows.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                Собирать нечего: заданий по юрлицу нет. Подтяните новые в меню —
                «Обновить заказы».
              </p>
            ) : (
              <ul className="h-full divide-y overflow-y-auto bg-white">
                {rows.map((row) => (
                  <PickRow
                    key={`${row.article}\u0000${row.characteristic_name}`}
                    row={row}
                    name={names?.[row.article] ?? row.nomenclature_name}
                    inRoom={room?.[row.article] ?? row.in_room ?? null}
                    justScanned={row.article === lastArticle}
                  />
                ))}
              </ul>
            )}

            {(blockedNotice || hero.kind !== "idle") && (
              <ScanBannerSlot>
                <div className="flex flex-col gap-2">
                  {/* Незасчитанный скан — РЯДОМ с героем последнего засчитанного,
                      а не вместо него: иначе «перепечатать стикер» пропадает. */}
                  {blockedNotice && (
                    <ScanBanner
                      tone="warn"
                      title="Скан не засчитан"
                      onClose={() => setBlockedNotice(null)}
                    >
                      <p className="mt-1 text-[13px] text-amber-900">{blockedNotice}</p>
                    </ScanBanner>
                  )}
                  {hero.kind !== "idle" && (
                    <ResultBanner
                      state={hero}
                      onClose={() => setHero({ kind: "idle" })}
                      onReprint={lastPickId ? () => reprintMut.mutate(lastPickId) : null}
                      reprinting={reprintMut.isPending}
                    />
                  )}
                </div>
              </ScanBannerSlot>
            )}
          </div>

          {/* Активный короб */}
          {activeBox ? (
            <button
              type="button"
              onClick={() => setSheet("box")}
              className={`border-t px-3 py-2 text-left text-sm ${
                blockReason || activeBox.overweight ? "bg-amber-50" : "bg-white"
              }`}
            >
              <span className="font-medium">Короб {activeBox.position}</span> · {activeBox.orders}{" "}
              {ordersWord(activeBox.orders)} · {boxWeightLabel(activeBox)}
              {activeBox.overweight && <span className="ml-2 text-rose-600">перевес</span>}
            </button>
          ) : (
            <div className="border-t bg-white px-3 py-2 text-xs text-muted-foreground">
              Короб откроется сам при первом скане. Грузоместа и этикетки заводятся
              одним заходом в конце поставки.
            </div>
          )}

          {/* Нижняя панель: кнопка камеры живёт ЗДЕСЬ, в зоне большого пальца,
              и ничего не перекрывает (плавающая кнопка ложилась на список). */}
          <nav
            className={cn("grid gap-1 border-t bg-white p-2", cameraAllowed ? "grid-cols-2" : "grid-cols-1")}
          >
            <Button
              variant="outline"
              className="h-12"
              onClick={() => activeBox && closeBoxMut.mutate(activeBox.box_id)}
              disabled={closeBoxMut.isPending || !activeBox}
              title="Отметить короб закрытым. Грузоместа и этикетки заводятся одним заходом на экране завершения поставки"
            >
              <BoxIcon className="mr-1 size-4" /> Закрыть короб
            </Button>
            {cameraAllowed && (
              <Button
                className="h-12 bg-sky-700 hover:bg-sky-800"
                onClick={() => setCameraOpen(true)}
              >
                <Camera className="mr-1 size-4" /> Сканировать камерой
              </Button>
            )}
          </nav>
        </>
      )}

      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-40 bg-black">
            <CameraScanner
              onDetect={(raw) => {
                setCameraOpen(false);
                onScan(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}

      {eanDraft && (
        <PartiaSheet
          code={eanDraft.code}
          defaultPartia={eanDraft.partia}
          onSubmit={(partia) => submitEan(eanDraft.code, partia, false)}
          onCancel={() => setEanDraft(null)}
        />
      )}

      {partiaMissing && (
        <Dialog
          title="Партии нет в 1С"
          // Текст причины даёт сервер (он знает и номер, и номенклатуру) —
          // здесь только то, что будет сделано по кнопке.
          body={
            `${partiaMissing.detail} Позиция примется с нулевой партией, ` +
            `введённый номер «${partiaMissing.partia}» сохранится.`
          }
          confirmLabel="Принять без партии"
          confirmBusy={pickMut.isPending}
          onConfirm={() => submitEan(partiaMissing.code, partiaMissing.partia, true)}
          // «Отмена» — отказ от учёта БЕЗ партии, а не от скана: возвращаем к
          // полю ввода с тем же номером, чтобы можно было исправить опечатку.
          // Штука всё ещё в руке, и терять её скан незачем.
          onCancel={() => {
            setEanDraft({ code: partiaMissing.code, partia: partiaMissing.partia });
            setPartiaMissing(null);
          }}
        />
      )}

      {sheet === "menu" && (
        <MenuSheet
          onClose={() => setSheet(null)}
          onSync={() => syncMut.mutate()}
          syncing={syncMut.isPending}
          issues={state?.issues.total ?? 0}
          onFix={() => setSheet("fix")}
          onReprint={lastPickId ? () => reprintMut.mutate(lastPickId) : null}
          onFinish={
            supply ? () => navigate(`/fbs/supplies/${supply.supply_id}/finish`) : null
          }
          wbSupplyId={supply?.wb_supply_id ?? null}
        />
      )}
      {sheet === "box" && activeBox && (
        <BoxSheet box={activeBox} onClose={() => setSheet(null)} onChanged={refetchState} />
      )}
      {sheet === "fix" && state && (
        <FixSheet
          cabinetId={cabinet}
          supplyId={supply?.supply_id ?? null}
          rows={state.issues.rows}
          onClose={() => setSheet(null)}
          onSgtinRemoved={refetchState}
          onReplacing={setReplacing}
          onRetried={(issues) => {
            // Мгновенный ответ шторке + перечитывание состояния: после починки
            // одной причины у позиции может вскрыться следующая.
            qc.setQueryData(stateKey, (old: fbs.PickState | undefined) =>
              old ? { ...old, issues } : old,
            );
            refetchState();
          }}
        />
      )}
    </div>
  );
}

/** Строка списка к сборке: сколько осталось, полоса прогресса и подсказка
 *  «сколько по учёту лежит в комнате». Полностью собранная строка не исчезает —
 *  она уезжает вниз и остаётся видимой как сделанная работа. */
function PickRow({
  row,
  name,
  inRoom,
  justScanned,
}: {
  row: fbs.PickListRow;
  /** Наименование из 1С (тянется отдельной холодной ручкой). */
  name: string;
  inRoom: number | null;
  justScanned: boolean;
}): React.ReactElement {
  const total = row.needed + row.picked;
  const pct = total > 0 ? Math.round((row.picked / total) * 100) : 100;
  const done = row.needed === 0;

  return (
    <li className={cn("px-3 py-2", done ? "bg-emerald-50/70" : "bg-white")}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[15px] font-bold">{row.article}</span>
        {/* Характеристика идёт СРАЗУ за артикулом и тем же кеглем: у одного
            артикула бывает несколько размеров, и именно она отвечает на
            вопрос «что брать с полки». Прятать её мелким шрифтом рядом с
            наименованием нельзя — там её не читают. */}
        {row.characteristic_name && (
          <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[13px] font-bold text-slate-900">
            {row.characteristic_name}
          </span>
        )}
        {justScanned && !done && (
          <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700">
            последний скан
          </span>
        )}
        <span className="ml-auto shrink-0 text-[13px]">
          {done ? (
            <span className="font-semibold text-emerald-700">собрано</span>
          ) : (
            <>
              <b className="text-[17px] tabular-nums">{row.needed}</b>{" "}
              <span className="text-slate-500">ост.</span>
            </>
          )}
        </span>
      </div>
      {/* Наименование — мелко и приглушённо, но ЦЕЛИКОМ: обрезка многоточием
          прячет как раз хвост, которым отличаются соседние позиции («…, 250 мл»
          против «…, 500 мл»). Строка при этом может стать выше — так и надо. */}
      {name && (
        <div className="break-words text-[11px] leading-tight text-slate-500">{name}</div>
      )}
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-slate-200">
        <div
          className={cn("h-full rounded", done ? "bg-emerald-500" : "bg-sky-500")}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
        <span>
          собрано {row.picked} из {total}
        </span>
        {inRoom != null && (
          <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">
            в комнате {fmtQty(inRoom)}
          </span>
        )}
      </div>
    </li>
  );
}

/**
 * Ввод партии для немаркированного товара (скан EAN13).
 *
 * Отдельный шаг между сканом и запросом: в штрихкоде партии нет, а без неё
 * позиция не попадёт в документы 1С. Запрос уходит только по «Принять» —
 * до этого в WB ничего не занято и стикер не напечатан.
 *
 * Показываем распознанный штрихкод: кладовщик должен видеть, что считалось
 * именно то, что он держит в руке.
 */
function PartiaSheet({
  code,
  defaultPartia,
  onSubmit,
  onCancel,
}: {
  code: string;
  /** Партия предыдущей штуки этого артикула — со склада берут подряд из одной. */
  defaultPartia: string;
  onSubmit: (partia: string) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [partia, setPartia] = useState(defaultPartia);
  const value = partia.trim();

  return (
    <BottomSheet onClose={onCancel} panelClassName="p-3">
      <h2 className="text-lg font-semibold">Товар без Честного знака</h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Штрихкод <span className="font-mono font-semibold text-slate-700">{code}</span> —
        партии в нём нет, введите номер с этикетки.
      </p>
      <label className="mt-3 block text-[13px] font-semibold">Номер партии</label>
      <input
        autoFocus
        value={partia}
        onChange={(e) => setPartia(e.target.value)}
        onKeyDown={(e) => {
          // Ввод с ТСД заканчивается Enter'ом — принимаем им же, не целясь в кнопку.
          if (e.key === "Enter" && value) onSubmit(value);
        }}
        placeholder="например 174"
        className="mt-1 h-12 w-full rounded-lg border px-3 text-[16px]"
      />
      {defaultPartia && (
        <p className="mt-1 text-[11px] text-slate-500">
          Подставлена партия предыдущей штуки этого артикула.
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <Button variant="outline" className="h-12 flex-[0.4]" onClick={onCancel}>
          Отмена
        </Button>
        <Button className="h-12 flex-1" onClick={() => onSubmit(value)} disabled={!value}>
          <Check className="mr-1 size-4" /> Принять
        </Button>
      </div>
    </BottomSheet>
  );
}

/** Плашка результата скана. Успех — компактно, ошибка и незакрытый хвост —
 *  крупно и цветно. Сама не гаснет: закрывает её рука или следующий скан. */
function ResultBanner({
  state,
  onClose,
  onReprint,
  reprinting,
}: {
  state: HeroState;
  onClose: () => void;
  onReprint: (() => void) | null;
  reprinting: boolean;
}): React.ReactElement | null {
  if (state.kind === "idle") return null;

  if (state.kind === "resolving") {
    return (
      <ScanBanner tone="info" title="Ищем товар…" onClose={null}>
        <p className="text-[12px] text-sky-900">Задание, стикер, печать</p>
      </ScanBanner>
    );
  }

  if (state.kind === "error") {
    return (
      <ScanBanner
        tone="error"
        title={
          state.cabinetName ? `Это товар «${state.cabinetName}»` : PICK_ERROR_TITLE[state.code]
        }
        onClose={onClose}
      >
        <p className="mt-1 text-[13px] text-red-900">{state.text}</p>
      </ScanBanner>
    );
  }

  const { result } = state;
  const warn = state.kind === "warn";
  return (
    <ScanBanner
      tone={warn ? "warn" : "ok"}
      title={result.article}
      onClose={onClose}
      actions={
        onReprint ? (
          <Button
            size="sm"
            variant="outline"
            className="h-10 flex-1 bg-white"
            onClick={onReprint}
            disabled={reprinting}
          >
            <Printer className="mr-1 size-4" /> Перепечатать стикер
          </Button>
        ) : undefined
      }
    >
      <p className="break-words text-[12px] leading-tight">{result.nomenclature_name}</p>
      {result.characteristic_name && (
        <p className="text-[12px]">Характеристика: {result.characteristic_name}</p>
      )}
      <p className="text-[12px]">
        Партия <b>{result.partia_name ?? "не определена"}</b>
        {result.expiration && ` · годен до ${result.expiration}`}
      </p>
      <p className="text-[11px] text-slate-600">
        Задание #{result.wb_order_id} ·{" "}
        {result.sticker_printed ? "стикер напечатан" : "стикер НЕ напечатан"} ·{" "}
        {result.box_position != null ? `короб ${result.box_position}` : "короб не создан"}
      </p>
      {result.warning && (
        // Предупреждений может быть несколько (печать, КИЗ, статус ЧЗ) — по строке на каждое.
        <p className="mt-1 whitespace-pre-line text-[13px] font-semibold">{result.warning}</p>
      )}
    </ScanBanner>
  );
}

/** Меню аппбара: всё, что не входит в скан-цикл, но нужно под рукой.
 *  Добора здесь нет намеренно — вход в него один, с главного экрана. */
function MenuSheet({
  onClose,
  onSync,
  syncing,
  issues,
  onFix,
  onReprint,
  onFinish,
  wbSupplyId,
}: {
  onClose: () => void;
  onSync: () => void;
  syncing: boolean;
  issues: number;
  onFix: () => void;
  onReprint: (() => void) | null;
  /** null — поставки ещё нет (не было ни одного скана), завершать нечего. */
  onFinish: (() => void) | null;
  wbSupplyId: string | null;
}): React.ReactElement {
  return (
    <BottomSheet onClose={onClose} panelClassName="p-3">
      <p className="mb-2 text-xs text-muted-foreground">
        Поставка {wbSupplyId ?? "будет создана первым сканом"}
      </p>
      <div className="grid gap-2">
        <Button
          variant="outline"
          className="h-12 justify-start"
          onClick={onSync}
          disabled={syncing}
        >
          <RefreshCw className={`mr-2 size-4 ${syncing ? "animate-spin" : ""}`} />
          Обновить заказы
        </Button>
        <Button variant="outline" className="h-12 justify-start" onClick={onFix}>
          <AlertTriangle className="mr-2 size-4" /> Разбор {issues > 0 ? `(${issues})` : ""}
        </Button>
        <Button
          variant="outline"
          className="h-12 justify-start"
          onClick={() => onReprint?.()}
          disabled={!onReprint}
          title={onReprint ? "" : "Нечего перепечатывать — не было ни одного скана"}
        >
          <Printer className="mr-2 size-4" /> Перепечатать последний стикер
        </Button>
        <Button
          className="h-12 justify-start"
          onClick={() => onFinish?.()}
          disabled={!onFinish}
          title={onFinish ? "" : "Поставки ещё нет — она создаётся первым сканом"}
        >
          <Check className="mr-2 size-4" /> Завершить поставку
        </Button>
      </div>
    </BottomSheet>
  );
}

/**
 * SH-FIX «Разбор». Сюда попадает то, что скан-цикл сознательно не стал ждать:
 * непечатанный стикер, неопределившаяся партия, непринятый WB срок годности.
 * Кнопка «Повторить» чинит одну причину за раз — если вскрывается следующая,
 * строка остаётся в списке уже с ней, а не исчезает как «починенная».
 *
 * Строки приходят из общего состояния экрана (`pick-state`), своего запроса у
 * шторки нет: «Разбор» — часть того же ответа.
 */
function FixSheet({
  cabinetId,
  supplyId,
  rows,
  onClose,
  onRetried,
  onSgtinRemoved,
  onReplacing,
}: {
  cabinetId: number;
  supplyId: string | null;
  rows: fbs.IssueRow[];
  onClose: () => void;
  onRetried: (issues: fbs.Issues) => void;
  /** «Отправить без кода» / «Заменить товар» прошли — строки «Разбора»
   *  перечитать из состояния экрана. */
  onSgtinRemoved: () => void;
  /** Открыт диалог «Заменить товар» — основной скан молчит. */
  onReplacing: (open: boolean) => void;
}): React.ReactElement {
  const [progress, setProgress] = useState<string>("");
  const retryMut = useMutation({
    mutationFn: (pickId: string) => fbs.retryIssue(pickId, cabinetId),
    onSuccess: (issues) => {
      onRetried(issues);
      toast.success(issues.total === 0 ? "Разбор пуст" : "Готово");
    },
    onError: (e) => toast.error(apiErrorText(e, "Повторить не удалось")),
  });

  // Цикл проходов — общий (`lib/fbs.ts::useRetryAllIssues`): копия в карточке
  // завершения уже разъезжалась с этой.
  const retryAllMut = useRetryAllIssues(supplyId, cabinetId, onRetried, setProgress);
  const busy = retryMut.isPending || retryAllMut.isPending;

  return (
    <BottomSheet onClose={onClose} panelClassName="max-h-[75vh] overflow-y-auto p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Разбор</h2>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Закрыть
        </Button>
      </div>
      {rows.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Ничего не осталось — все позиции закрыты.
        </p>
      )}

      {rows.length > 1 && supplyId && (
        <div className="mb-2 flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => retryAllMut.mutate()}
            disabled={busy}
            className="flex-1"
          >
            {retryAllMut.isPending ? "Чиню…" : `Повторить всё (${rows.length})`}
          </Button>
          {progress && <span className="text-xs text-muted-foreground">{progress}</span>}
        </div>
      )}

      {ISSUE_GROUPS.map((group) => {
        const groupRows = rows.filter((r) => r.issue === group.key);
        if (groupRows.length === 0) return null;
        return (
          <section key={group.key} className="mb-3">
            <h3 className="text-sm font-semibold">
              {group.title} · {groupRows.length}
            </h3>
            <p className="text-xs text-muted-foreground">{group.hint}</p>
            <ul className="mt-1 divide-y">
              {groupRows.map((row) => (
                <li key={row.pick_id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <div className="flex-1">
                    <div className="font-medium">{row.article}</div>
                    <div className="text-xs text-muted-foreground">
                      #{row.wb_order_id} · партия {row.partia_name ?? "—"}
                      {row.box_position != null && ` · короб ${row.box_position}`}
                    </div>
                    {row.issue === "sgtin_rejected" && row.sgtin_decision_text && (
                      <div className="text-xs text-rose-700">{row.sgtin_decision_text}</div>
                    )}
                  </div>
                  {row.issue === "sgtin_rejected" && (
                    <RemoveSgtinButton
                      pickId={row.pick_id}
                      what={`${row.article} · задание #${row.wb_order_id}`}
                      disabled={busy}
                      onRemoved={onSgtinRemoved}
                    />
                  )}
                  {row.issue === "sgtin_rejected" && (
                    <ReplaceSgtinButton
                      row={row}
                      disabled={busy}
                      onOpenChange={onReplacing}
                      onReplaced={onSgtinRemoved}
                    />
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => retryMut.mutate(row.pick_id)}
                    disabled={busy}
                  >
                    Повторить
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      <p className="mt-2 text-xs text-muted-foreground">
        Позиция без партии не попадёт в документы продажи — поставку лучше закрывать
        с пустым разбором.
      </p>
    </BottomSheet>
  );
}

function BoxSheet({
  box,
  onClose,
  onChanged,
}: {
  box: fbs.Box;
  onClose: () => void;
  onChanged: () => void;
}): React.ReactElement {
  const itemsQ = useQuery({
    queryKey: ["fbs-box-items", box.box_id],
    queryFn: () => fbs.getBoxItems(box.box_id),
  });
  const reprintMut = useMutation({
    mutationFn: () => fbs.reprintBoxQr(box.box_id),
    onSuccess: (r) => (r.ok ? toast.success("QR отправлен на печать") : toast.error(r.error ?? "")),
    onError: (e) => toast.error(apiErrorText(e, "Печать не удалась")),
  });
  // ⚠️ Перепечатка С ЛЮБОЙ СТРОКИ, а не только у последнего скана.
  // Боевой случай 11.09.2026: портал доложил «напечатано» (агент принял байты
  // сокетом принтера), а наклейка физически не вышла — лента, замин, носитель.
  // Такой скан не попадает в «Разбор»: портал о провале НЕ ЗНАЕТ. Карточку
  // последнего скана к тому времени закрыли, и перепечатать было НЕОТКУДА —
  // склад встал. Стикер ВБ отдаёт сколько угодно раз для задания в `confirm`.
  const reprintPickMut = useReprintSticker();
  const removeMut = useMutation({
    mutationFn: (pickId: string) => fbs.deletePick(pickId),
    onSuccess: () => {
      onChanged();
      itemsQ.refetch();
      toast.success("Позиция убрана, задание снова к сборке");
    },
    onError: (e) => toast.error(apiErrorText(e, "Не удалось убрать позицию")),
  });
  const closeMut = useMutation({
    mutationFn: () => fbs.closeBox(box.box_id),
    onSuccess: () => {
      onChanged();
      onClose();
      toast.success(`Короб ${box.position} закрыт`);
    },
    onError: (e) => toast.error(apiErrorText(e, "Не удалось закрыть короб")),
  });
  const blockReason = boxBlockReason(box);

  return (
    <BottomSheet onClose={onClose} panelClassName="max-h-[75vh] overflow-y-auto p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Короб {box.position}</h2>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Закрыть
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {box.wb_trbx_id ?? "этикетка печатается в конце поставки"} · {boxWeightLabel(box)}
      </p>
      {blockReason && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">{blockReason}</p>
      )}
      <p className="mt-2 rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">
        Стикеры заказов на транспортный короб клеить нельзя — только QR грузоместа.
      </p>

      {(itemsQ.isLoading || itemsQ.isError) && (
        <LoadState
          isLoading={itemsQ.isLoading}
          error={itemsQ.error}
          onRetry={() => itemsQ.refetch()}
          className="flex flex-col items-center gap-2 py-6 text-center"
        />
      )}
      <ul className="mt-2 divide-y">
        {(itemsQ.data ?? []).map((item) => (
          <li key={item.pick_id} className="flex items-center gap-2 py-2 text-sm">
            <div className="flex-1">
              <div className="font-medium">{item.article}</div>
              <div className="text-xs text-muted-foreground">
                #{item.wb_order_id} · партия {item.partia_name ?? "—"}
                {item.issue && <span className="ml-1 text-amber-700">· {item.issue}</span>}
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              title="Перепечатать стикер заказа"
              disabled={reprintPickMut.isPending}
              onClick={() => reprintPickMut.mutate(item.pick_id)}
            >
              <Printer className="size-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => removeMut.mutate(item.pick_id)}>
              <X className="size-4 text-destructive" />
            </Button>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => reprintMut.mutate()}
          disabled={!box.wb_trbx_id}
          title={box.wb_trbx_id ? "" : "Этикетки печатаются одним заходом в конце поставки"}
        >
          <Printer className="mr-1 size-4" /> Перепечатать QR
        </Button>
        <Button
          size="sm"
          onClick={() => closeMut.mutate()}
          disabled={!!blockReason || closeMut.isPending}
        >
          Закрыть короб
        </Button>
      </div>
    </BottomSheet>
  );
}
